import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags.js'
import * as nodes from '/vercel/path0/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: FAQ\npageTitle: Frequently Asked Questions\ndescription: Frequently Asked Questions\n---\n\n## Frequently Asked Questions\n\n### Can I create a project using the API?\n\nNot currently. We are working on this feature and will be releasing it soon.\n\n### How do I remove the Powered by Mendable?\n\nTo remove the Powered by Mendable, you need to upgrade to an enterprise or custom plan. Contact us at garrett@mendable.ai and we can help you out.\n\n\n### Is it free?\n\nWe have a free plan that gives you 500 message credits. It is also free for certain Open source projects. Contact us to see if your project is eligible.\n\n### How do I get an anon key?\n\nTo get your anon key you need to sign up at mendable.ai and create a project. Then you can find your anon key in the API Keys section of the dashboard. Anon keys are used for client-side while API keys are used for server-side.\n\n### Can you correct the AI response?\n\nYes, Mendable offers a 'teach the model' functionality where you can correct the AI response and it will learn from it.\n\n### Is it 100% accurate?\n\nLike Humans, AI will never be 100% accurate. So we can't assure you that every solution will be correct.\n\n### How do I cancel my subscription?\n\nSimply log into our platform, go to your account and click on \"Open customer portal\" button. There you will be able to cancel/modify it through Stripe.\n\n### How does Mendable work?\n\nOur application syncs with your documentation and support channels, then uses your docs and previously answered questions to suggest possible answers.\n\n### How does Mendable price custom plans?\n\n1. Use case\nMendable differentiates between internal and external use cases.\nWith Mendable, we give you the ability to use our chat bots for a variety of use cases, both for internal efficiency and external communication to your customers.\n\n2. Total usage\nFor specifically external use cases, you will only pay for the value you're receiving.\nMendable will look at the total number of messages sent during a month.\n\n3. Custom work\nIf there are any special feature requests (custom data connectors, etc.), we are happy to discuss these requirements!\n\n### Integrating Mendable Components with React 17\n\nTo integrate Mendable components into your React 17 application, start by including the Mendable script in your project. Insert the following line in the `<head>` section of your `index.html` file:\n\n```html\n<script src=\"https://unpkg.com/@mendable/search@0.0.206-beta.2/dist/umd/mendable-bundle.min.js\"></script>\n```\n\nNext, use the `useEffect` hook within your main App component to initialize Mendable. Here's how you can do it:\n\n```jsx\nimport { useEffect } from 'react';\n\nfunction App() {\n  useEffect(() => {\n    const initializeMendable = () => {\n      Mendable.initialize({\n        anon_key: \"YOUR_ANON_KEY\",\n        type: 'searchBar',\n        elementId: 'mendable-component'\n      });\n    };\n\n    if (document.readyState === \"complete\" || document.readyState === \"interactive\") {\n      setTimeout(() => {\n        initializeMendable();\n      }, 1);\n    } else {\n      document.addEventListener(\"DOMContentLoaded\", () => {\n        initializeMendable();\n      });\n    }\n    const Mendable = window.Mendable;\n  }, []);\n\n  return (\n    <div className=\"App\">\n      <header className=\"App-header\">\n        <div id=\"mendable-component\"></div>\n        ...\n      </header>\n    </div>\n  );\n}\n\nexport default App;\n```\n\nThis setup ensures that the Mendable component is correctly initialized and ready to use within your React 17 application.";
const filepath = "/faq.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
